import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Clock, User, Building2, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { baseURL } from '@/lib/axios';

type ProjectStatus = string | { name?: string | null } | null;

interface Project {
  id: string;
  name: string;
  client: string;
  status: ProjectStatus;
  priority: string;
  progress: number;
  description: string;
  startDate: string;
  endDate: string;
  createdDate: string;
  projectManager?: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    employeeId: string;
    profilePicture?: string | null;
  } | string;
  team: Array<{
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    employeeId: string;
    profilePicture?: string | null;
  }> | string[];
  tags: string[];
  totalLoggedHours: number;
  hoursByDay: Array<{ day: string; hours: number }>;
}

interface OverviewTabProps {
  project: Project;
}

const getStatusColor = (status: ProjectStatus) => {
  // Extract status name if it's an object
  let statusName: string | null = null;
  if (status && typeof status === 'object' && 'name' in status) {
    statusName = status.name ?? null;
  } else if (typeof status === 'string') {
    statusName = status;
  }
  
  // Normalize status to uppercase for comparison
  const statusUpper = String(statusName || '').toUpperCase();
  
  switch (statusUpper) {
    case 'IN_PROGRESS':
    case 'IN PROGRESS':
    case 'ACTIVE':
    case 'In Progress':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'COMPLETED':
    case 'COMPLETE':
    case 'Completed':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'ON_HOLD':
    case 'ON HOLD':
    case 'On Hold':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'PLANNING':
    case 'Planning':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'TESTING':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'CANCELLED':
    case 'CANCELED':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'High':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'Low':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const apiBaseWithoutPrefix = baseURL ? baseURL.replace(/\/api\/v1\/?$/, '') : '';

const buildImageUrl = (path: string) => {
  if (!apiBaseWithoutPrefix) {
    return path;
  }
  const normalizedBase = apiBaseWithoutPrefix.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

const getProfileImageUrl = (profilePicture?: string | null) => {
  if (!profilePicture) return null;
  if (profilePicture.startsWith('http')) {
    return profilePicture;
  }
  return buildImageUrl(profilePicture);
};

const getInitials = (firstName?: string, lastName?: string, email?: string, fallback?: string) => {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.trim();
  if (initials) {
    return initials.toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  if (fallback) {
    return fallback.substring(0, 2).toUpperCase();
  }
  return 'NA';
};

export default function OverviewTab({ project }: OverviewTabProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const managerDetails = (() => {
    if (!project.projectManager) return null;
    if (typeof project.projectManager === 'object') {
      return project.projectManager;
    }
    return {
      _id: project.projectManager,
      email: '',
      firstName: project.projectManager,
      lastName: '',
      employeeId: '',
      profilePicture: null,
    };
  })();

  const getProjectManagerName = () => {
    if (!managerDetails) return 'Not assigned';
    const fullName = `${managerDetails.firstName ?? ''} ${managerDetails.lastName ?? ''}`.trim();
    return fullName || managerDetails.email || 'Not assigned';
  };

  const getTeamMembers = () => {
    if (!project.team || project.team.length === 0) return [];
    if (typeof project.team[0] === 'object') {
      return (project.team as Array<{
        firstName: string;
        lastName: string;
        _id: string;
        email?: string;
        profilePicture?: string | null;
      }>).map((member) => ({
        name: `${member.firstName} ${member.lastName}`.trim() || member.email || 'Team member',
        id: member._id,
        email: member.email,
        profilePicture: member.profilePicture,
        firstName: member.firstName,
        lastName: member.lastName,
      }));
    }
    return (project.team as string[]).map((member, index) => ({
      name: member,
      id: `${member}-${index}`,
      email: '',
      profilePicture: null,
      firstName: member,
      lastName: '',
    }));
  };

  const teamMembers = getTeamMembers();

  return (
    <div className="space-y-6">
      {/* Project Status and Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Project Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Status</span>
                <Badge className={cn('px-3 py-1', getStatusColor(project.status))}>
                  {typeof project.status === 'object' && project.status !== null && 'name' in project.status 
                    ? project.status.name 
                    : String(project.status || '')}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Priority</span>
                <Badge className={cn('px-3 py-1', getPriorityColor(project.priority))}>
                  {project.priority}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Completion</span>
                <span className="text-sm font-semibold text-gray-900">{project.progress}%</span>
              </div>
              <Progress value={project.progress} className="h-2" />
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Started {formatDate(project.startDate)}</span>
                <span>Due {formatDate(project.endDate)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Total Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <span className="text-2xl font-bold text-gray-900">{project.totalLoggedHours}</span>
                <span className="text-sm text-gray-600">hours logged</span>
              </div>
              <div className="flex items-center space-x-1 text-sm text-green-600">
                <TrendingUp className="h-4 w-4" />
                <span>+12% this week</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Building2 className="h-5 w-5" />
              <span>Project Details</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Project Name</label>
                <p className="text-sm text-gray-900 mt-1">{project.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Customer</label>
                <p className="text-sm text-gray-900 mt-1">{project.client}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Start Date</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(project.startDate)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">End Date</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(project.endDate)}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Description</label>
              <p className="text-sm text-gray-900 mt-1">{project.description}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Tags</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {(project.tags || []).map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>Team & Management</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Project Manager</label>
              <div className="flex items-center gap-3 mt-2">
                <div className="w-10 h-10 rounded-full bg-gray-200 border border-gray-300 flex items-center justify-center overflow-hidden text-sm font-semibold text-gray-600">
                  {managerDetails && getProfileImageUrl(managerDetails.profilePicture) ? (
                    <img
                      src={getProfileImageUrl(managerDetails.profilePicture)!}
                      alt={getProjectManagerName()}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <span className={`${managerDetails && getProfileImageUrl(managerDetails.profilePicture) ? 'hidden' : ''}`}>
                    {getInitials(managerDetails?.firstName, managerDetails?.lastName, managerDetails?.email, getProjectManagerName())}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-900 font-medium">{getProjectManagerName()}</span>
                  {managerDetails?.email ? (
                    <span className="text-xs text-gray-500">{managerDetails.email}</span>
                  ) : null}
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Team Members</label>
              {teamMembers.length > 0 ? (
                <div className="flex flex-wrap gap-3 mt-3">
                  {teamMembers.map((member) => {
                    const imageUrl = getProfileImageUrl(member.profilePicture || undefined);
                    return (
                      <div key={member.id} className="flex items-center gap-2 pr-3 py-1 rounded-full border border-gray-200 bg-white shadow-sm">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden text-xs font-semibold text-gray-600">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={member.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <span className={`${imageUrl ? 'hidden' : ''}`}>
                            {getInitials(member.firstName, member.lastName, member.email, member.name)}
                          </span>
                        </div>
                        <span className="text-xs font-medium text-gray-700">{member.name}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 mt-2">No team members assigned.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hours Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Logged Hours by Day</span>
          </CardTitle>
          <CardDescription>
            Daily hours logged over the past week
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={project.hoursByDay || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => [`${value} hours`, 'Hours']}
                  labelFormatter={(label) => `${label}day`}
                />
                <Bar dataKey="hours" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}